import axiosClient from './axiosClient';
import { API_BASE_URL, TOKEN_KEY } from '@/utils/constants';
import { Subtask, TaskWorklog } from '@/types';

export interface CreateSubtaskData {
  title: string;
  description?: string;
  task_type_id?: string;
  priority_id?: string;
}

export interface UpdateSubtaskData {
  title?: string;
  description?: string;
  status?: string;
  order?: number;
  is_completed?: boolean;
  // Extended fields support
  workflow_status_id?: string;
  assigned_to?: string;
  reporter_id?: string;
  priority_id?: string;
}

export const subtaskAPI = {
  async list(taskId: string): Promise<Subtask[]> {
    try {
      const res = await axiosClient.get<Subtask[]>(`/tasks/${taskId}/subtasks`);
      return res.data;
    } catch (err) {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_BASE_URL}/tasks/${taskId}/subtasks`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });
      if (!res.ok) return [];
      return await res.json();
    }
  },

  async create(taskId: string, data: CreateSubtaskData): Promise<Subtask> {
    const res = await axiosClient.post<Subtask>(`/tasks/${taskId}/subtasks`, data);
    return res.data;
  },

  async get(subtaskId: string): Promise<Subtask> {
    const res = await axiosClient.get<Subtask>(`/subtasks/${subtaskId}`);
    return res.data;
  },

  async update(subtaskId: string, data: UpdateSubtaskData): Promise<Subtask> {
    const res = await axiosClient.put<Subtask>(`/subtasks/${subtaskId}`, data);
    return res.data;
  },

  async remove(subtaskId: string): Promise<void> {
    await axiosClient.delete(`/subtasks/${subtaskId}`);
  },

  async bulkDelete(subtaskIds: string[]): Promise<void> {
    // Delete subtasks one by one
    await Promise.all(subtaskIds.map(id => axiosClient.delete(`/subtasks/${id}`)));
  },

  // Worklog methods
  async getWorklogs(subtaskId: string): Promise<TaskWorklog[]> {
    const response = await axiosClient.get<TaskWorklog[]>(`/subtasks/${subtaskId}/worklogs`);
    return response.data;
  },

  async addWorklog(subtaskId: string, data: {
    time_spent_minutes: number;
    started_at?: string;
    comment?: string;
    adjust_remaining?: 'auto' | 'set' | 'none';
    remaining_time_minutes?: number | null;
  }): Promise<{ worklog: TaskWorklog; subtask: Subtask }> {
    const payload = {
      adjust_remaining: data.adjust_remaining ?? 'auto',
      ...data,
    };
    const response = await axiosClient.post<{ worklog: TaskWorklog; subtask: Subtask }>(`/subtasks/${subtaskId}/worklogs`, payload);
    return response.data;
  },
};


